// The catalog switch, as the web app sees it.
//
// HioBuy-only mode = the market routes exist in this build (MARKET_PREVIEW)
// AND market_settings.marketplace_public is on (read through the public RPC
// market_public_state). In that mode the LEGACY catalog (factory_products,
// products, factory pages, supplier product grids) is hidden from customers on
// the web — routes redirect to the marketplace, nav links point there. Nothing
// is deleted; suppliers and admins keep every legacy page. Turning the setting
// off restores everything, no deploy.
//
// Fails CLOSED towards the legacy catalog: any error, or the RPC missing
// (production before the migration), means "off" — customers keep what they
// see today.
//
// Not covered: the iOS app reads the legacy objects directly; mirroring this
// switch there is mobile work (its own session).

import { useEffect, useState } from 'react';
import { MARKET_PREVIEW } from './featureFlags';
import { marketDb } from './marketDb';

let cached = null;            // Promise<{ marketplace_public, checkout_enabled, preview, min_cart_sar }>
const listeners = new Set();

// The tiered service fee (market_fee_tiers): this viewer's ladder. Cards and pages price at
// its FIRST (highest) rate — the trader never pays more than they see; the cart applies the
// rate of its base (the delivered total). One flat step while the tiers are switched off.
function withFees(delivered, sched) {
  const tiers = Array.isArray(sched?.tiers) && sched.tiers.length ? sched.tiers.map((t) => ({ from_sar: Number(t.from_sar), rate: Number(t.rate) })) : null;
  if (!tiers) return delivered;
  // the smallest saving worth advertising (owner, 2026-10-31: 3%, editable)
  const minSaving = Number(sched.advertise_min_saving);
  return { ...delivered, fee_rate: tiers[0].rate, fee_tiers: tiers, fee_tiered: sched.tiered === true,
    advertise_min_saving: Number.isFinite(minSaving) && minSaving >= 0 ? minSaving : 0.03,
    // Rule C: the hours staff take to confirm a shipping price (and the trader has to pay it)
    confirm_hours: Number(sched.confirm_hours) > 0 ? Number(sched.confirm_hours) : 48 };
}

export function fetchMarketState({ refresh = false } = {}) {
  if (!MARKET_PREVIEW) return Promise.resolve({ marketplace_public: false, checkout_enabled: false, preview: false, min_cart_sar: 0 });
  if (!cached || refresh) {
    cached = Promise.all([marketDb.rpc('market_public_state'), marketDb.rpc('market_fee_schedule').then((r) => r, () => ({ data: null }))]).then(
      ([{ data, error }, fees]) => (error || !data ? { marketplace_public: false, checkout_enabled: false, preview: false, min_cart_sar: 0 }
        : { marketplace_public: data.marketplace_public === true, checkout_enabled: data.checkout_enabled === true, preview: data.preview === true,
          // the minimum cart value (owner, 2026-10-29); the server enforces it as well
          min_cart_sar: Number(data.min_cart_sar) > 0 ? Number(data.min_cart_sar) : 0,
          // the pricing config (owner, 2026-11-02: the switch is split): the fee, the allowance and
          // the rounding ALWAYS apply to product prices; delivered_enabled only decides whether the
          // cart calculates shipping options (`shipping`) — on, or for an admin previewing it
          delivered: data.delivered ? { ...withFees(data.delivered, fees?.data), shipping: data.delivered.enabled === true || data.preview === true } : null }),
      () => ({ marketplace_public: false, checkout_enabled: false, preview: false, min_cart_sar: 0 }),
    );
    cached.then(() => listeners.forEach((f) => f()));
  }
  return cached;
}

/** { ready, hioBuyOnly } — hioBuyOnly is false until the answer is known. */
export function useHioBuyOnly() {
  const [s, setS] = useState({ ready: !MARKET_PREVIEW, hioBuyOnly: false });
  useEffect(() => {
    if (!MARKET_PREVIEW) return undefined;
    let alive = true;
    const read = () => fetchMarketState().then((v) => { if (alive) setS({ ready: true, hioBuyOnly: v.marketplace_public }); });
    read();
    listeners.add(read);
    return () => { alive = false; listeners.delete(read); };
  }, []);
  return s;
}

/** The pricing config (fee, allowance, rounding, rates); `.shipping` says whether the cart calculates shipping. */
export function useDeliveredConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    if (!MARKET_PREVIEW) return undefined;
    let alive = true;
    const read = () => fetchMarketState().then((v) => { if (alive) setCfg(v.delivered || null); });
    read();
    listeners.add(read);
    return () => { alive = false; listeners.delete(read); };
  }, []);
  return cfg;
}

/** Who keeps the legacy catalog pages in HioBuy-only mode. */
export const keepsLegacyCatalog = (profile) => ['supplier', 'admin', 'super_admin'].includes(String(profile?.role || '').toLowerCase());
